import { useMemo, useState } from 'react';
import { Box, ChevronLeftIcon, ChevronRightIcon, FlatList, HStack, IconButton, Pressable } from 'native-base';
import { isUndefined, range } from 'lodash';
import moment from 'moment';

import { Text } from '@pimm/base';

const CALENDAR_WEEKS = 6;
// Compact rhythm for range pickers — 28px cells vs CalendarPicker's 34px.
const CALENDAR_DAY_WIDTH = 28;
const CALENDAR_WEEK_DAY_HEIGHT = 30;
const CALENDAR_WIDTH = 252;
const CALENDAR_HEIGHT = CALENDAR_DAY_WIDTH * CALENDAR_WEEKS + CALENDAR_WEEK_DAY_HEIGHT;

type CalendarPickerMode = 'single' | 'week';

export type CalendarPickerProps = {
  date?: Date;
  dimension?: {
    width?: number;
    height?: number;
  };
  mode?: CalendarPickerMode;
  today?: Date;
  min?: Date;
  max?: Date;
  utcOffset?: string;
  weekday?: number;
  renderDay?: (day: Date, options: { isToday?: boolean; isActive: boolean }) => React.ReactNode;
  onChange?: (date: Date, endDate?: Date) => void;
};

/**
 * EquipmentCalendarPicker — compact calendar for date-range pickers.
 *
 * Same API as CalendarPicker. Key differences:
 * - 28px cells (vs 34px) — compact rhythm for side-by-side range popover
 * - No gray ring on the focused date — renderDay owns all date highlights
 *   in range-picker context; CalendarPicker's ring stamps a phantom
 *   selection on whatever day happens to be internally focused
 * - Square hover (borderRadius: 0) — matches range-endpoint styling
 */
export const CalendarPicker = ({ max, min, mode = 'single', utcOffset = '', ...props }: CalendarPickerProps) => {
  const [weekday] = useState(isUndefined(props.weekday) ? 1 : props.weekday);
  const [currentDate, setCurrentDate] = useState<Date>(props.date ?? props.today ?? new Date());
  const [selectedDate, setSelectedDate] = useState<Date>(currentDate);

  const month = useMemo(() => {
    let startOfDate, endOfDate;
    const monthStartDate = moment(currentDate).startOf('month');
    const monthEndDate = moment(currentDate).endOf('month');
    const weekStart = monthStartDate.week();
    const weekEnd = monthEndDate.week();

    startOfDate = moment(currentDate).week(weekStart).startOf('week').weekday(weekday);
    endOfDate = moment(currentDate).week(weekEnd).endOf('week');

    // Handle year-boundary edge case
    if (endOfDate.toDate() < startOfDate.toDate()) {
      endOfDate = endOfDate.clone().add(1, 'years').week(1).endOf('week');
    }

    return {
      numOfDays: endOfDate.diff(startOfDate, 'days'),
      startOfDate: startOfDate.toDate(),
      endOfDate: endOfDate.toDate(),
    };
  }, [currentDate]);

  const week = useMemo(() => {
    if (mode !== 'week' || !selectedDate) return undefined;
    const startOfWeek = moment(selectedDate).startOf('week').weekday(1);
    return {
      startOfWeek: startOfWeek.toDate(),
      endOfDate: startOfWeek.add(6, 'days').toDate(),
    };
  }, [selectedDate, weekday]);

  const handleChangeDate = (date: Date) => () => {
    if (props.onChange) {
      if (mode === 'week' && week) {
        const day = moment(date);
        const startOfWeek = day.startOf('week').weekday(weekday);
        const endOfWeek = startOfWeek.clone().add(6, 'days');
        props.onChange(startOfWeek.toDate(), endOfWeek.toDate());
      } else props.onChange(date);
    }
    setSelectedDate(date);
  };

  const handleChangeMonth = (step: number) => () => {
    setCurrentDate(moment(currentDate).add(step, 'month').toDate());
  };

  return (
    <Box pt={3} pb={2} px={2}>
      <HStack flexDirection="row" alignItems="center" justifyContent="space-between" style={{ width: CALENDAR_WIDTH }} px={1}>
        <IconButton
          onPress={handleChangeMonth(-1)}
          _hover={{ backgroundColor: 'gray.100' }}
          icon={<ChevronLeftIcon size={4} color="gray.700" />}
        />
        <Text size="lg" fontWeight={600} color="black">
          {moment(currentDate).format('MMM YYYY')}
        </Text>
        <IconButton
          onPress={handleChangeMonth(1)}
          _hover={{ backgroundColor: 'gray.100' }}
          icon={<ChevronRightIcon size={4} color="gray.700" />}
        />
      </HStack>

      <FlatList
        width={CALENDAR_WIDTH}
        height={CALENDAR_HEIGHT}
        data={range(6)}
        style={{ height: CALENDAR_HEIGHT }}
        ListHeaderComponent={() => (
          <HStack w="full">
            {range(0, 7).map(index => {
              const day = moment(month.startOfDate).add(index, 'days');
              return (
                <Box key={index} flex={1} alignItems="center" justifyContent="center" w={CALENDAR_WIDTH / 7} h={CALENDAR_WEEK_DAY_HEIGHT}>
                  <Text size="sm" fontWeight={500} color="gray.500">
                    {day.format('ddd')}
                  </Text>
                </Box>
              );
            })}
          </HStack>
        )}
        renderItem={({ item, index }) => {
          const startingDays = index * 7;
          let isActiveWeek: boolean = false;

          if (mode === 'week') {
            const startOfWeek = moment(month.startOfDate).add(startingDays, 'days').weekday(weekday);
            const endOfWeek = startOfWeek.clone().add(6, 'days').endOf('day');
            isActiveWeek = moment(selectedDate).isBetween(startOfWeek, endOfWeek, null, '[]');
          }

          return (
            <HStack
              key={index}
              w="full"
              backgroundColor={isActiveWeek ? 'gray.200' : undefined}
              borderRadius={isActiveWeek ? CALENDAR_WEEK_DAY_HEIGHT : undefined}
            >
              {range(startingDays, startingDays + 7).map(days => {
                const day = moment(month.startOfDate).add(days, 'days');
                const isToday = day.isSame(props.today ?? new Date(), 'days');
                const isSameMonth = day.isSame(moment(currentDate), 'months');
                const isActiveDate = day.isSame(moment(selectedDate || currentDate), 'days');
                const textColor = isSameMonth ? 'black' : 'gray.500';
                const itemDay = props.renderDay
                  ? props.renderDay(day.toDate(), {
                      isActive: isActiveDate,
                      isToday: isToday,
                    })
                  : undefined;

                return (
                  <Pressable
                    key={startingDays + days}
                    display="flex"
                    alignItems="center"
                    justifyContent="center"
                    disabled={(min && day.toDate() < min) || (max && day.toDate() > max)}
                    onPress={handleChangeDate(day.toDate())}
                    style={{
                      height: (CALENDAR_HEIGHT - CALENDAR_WEEK_DAY_HEIGHT) / CALENDAR_WEEKS,
                      width: CALENDAR_WIDTH / 7,
                    }}
                    _disabled={{
                      opacity: 0.2,
                    }}
                    _hover={
                      !isActiveWeek
                        ? {
                            backgroundColor: 'gray.100',
                            borderRadius: 0,
                          }
                        : undefined
                    }
                  >
                    {itemDay || (
                      <Text size="md" color={isToday ? 'primary.600' : textColor}>
                        {day.toDate().getDate()}
                      </Text>
                    )}
                  </Pressable>
                );
              })}
            </HStack>
          );
        }}
        scrollEnabled={false}
        numColumns={1}
      />
    </Box>
  );
};
